import { PotentialBooking } from '@sales-app/graphql/queries/potentialBooking/potentialBookings.queries'
import { RecommendedHomeRow } from '@sales-app/hooks/useFavoritesPinnedHomes/useFavoritesPinnedHomes.util'
import { ReservationType } from '@sales-app/views/PotentialBooking/PotentialBooking/ControlSection/Filters/ReservationTypeSelection'
import { snakeCase } from 'change-case'
import { addMonths, isValid } from 'date-fns'

import { formatDate, parseDate } from '@/util'

import { HomeFilters } from './workspace.type'

export interface SearchAPIQueryParams {
  air_conditioned?: boolean
  available_from_end_date?: string
  balcony?: boolean
  baths?: string
  beds?: string
  elevator?: boolean
  end_date?: string
  gym?: boolean
  ne_latitude?: string
  ne_longitude?: string
  neighborhood_id?: string
  page?: string
  parking?: boolean
  pets?: boolean
  pool?: boolean
  property_slug?: string
  results_per_page?: string
  sort_by?: string
  start_date?: string
  sw_latitude?: string
  sw_longitude?: string
}

export const queryParamArrays = ['amenity', 'beds']
export const dateParams = ['moveInStartDate', 'moveOutStartDate', 'moveOutEndDate']
export const homeQueryParams = [
  'sortBy',
  'swLatitude',
  'swLongitude',
  'neLatitude',
  'neLongitude',
  'grouped',
  'use_true_pagination',
  'hqStay',
  'propertySlug',
  'property_slug',
  'minPrice',
  'maxPrice',
  ...queryParamArrays,
  ...dateParams,
]

export const propertyQueryParams = [
  'property_slug',
  'swLatitude',
  'swLongitude',
  'neLatitude',
  'neLongitude',
  'hqStay',
  'minPrice',
  'maxPrice',
  ...queryParamArrays,
  ...dateParams,
]

export const favoritePinnedQueryParams = [...dateParams]

const defaultAPIParams = {
  grouped: 'true',
  use_true_pagination: 'true',
}

type IDefaultAPIParams = typeof defaultAPIParams

export function transformHomeFilterToHomeAPIQuery(homeFilters: HomeFilters): SearchAPIQueryParams {
  return transformToAPIQuery(homeFilters, homeQueryParams, defaultAPIParams)
}

export function transformToPropertyAPIQuery(homeFilters: HomeFilters): SearchAPIQueryParams {
  return transformToAPIQuery(homeFilters, propertyQueryParams)
}

export function transformToFavoritePinnedAPIQuery(homeFilters: HomeFilters): SearchAPIQueryParams {
  return transformToAPIQuery(homeFilters, favoritePinnedQueryParams)
}

function transformToAPIQuery(
  homeFilters: HomeFilters,
  queryParamsList: Array<string>,
  defaultAPIParams?: IDefaultAPIParams,
): SearchAPIQueryParams {
  return Object.entries({ ...defaultAPIParams, ...homeFilters }).reduce((acc, [key, value]) => {
    const isAPIQueryParam = queryParamsList.includes(key)
    if (!isAPIQueryParam || !value) {
      return { ...acc }
    }

    const apiKey = snakeCase(key)

    if (key === 'moveInStartDate') {
      const availableFromEndDate = isValid(value) ? addMonths(new Date(value as Date), 1) : undefined
      return {
        ...acc,
        start_date: formatDate(value, 'yyyy-MM-dd'),
        available_from_end_date: availableFromEndDate ? formatDate(availableFromEndDate, 'yyyy-MM-dd') : undefined,
      }
    }

    if (key === 'amenity' && Array.isArray(value)) {
      let amenityObj = {}
      value.forEach((v) => {
        amenityObj[`${v}`] = 'true'
      })
      return { ...acc, ...amenityObj }
    }

    if (key === 'beds') {
      const beds = Array.isArray(value) ? value.join(',') : value
      return { ...acc, beds }
    }

    if (key === 'minPrice') {
      return { ...acc, price_gte: value }
    }

    if (key === 'maxPrice') {
      return { ...acc, price_lte: value }
    }

    if (key === 'moveOutStartDate') {
      if (!homeFilters.moveOutEndDate) {
        return { ...acc, end_date: formatDate(value, 'yyyy-MM-dd') }
      } else {
        return { ...acc }
      }
    }

    if (key === 'moveOutEndDate') {
      return { ...acc, end_date: formatDate(value, 'yyyy-MM-dd') }
    }

    return { ...acc, [apiKey]: value }
  }, {})
}

export function transformCoreAmenityToRusty(amenity?: string[]) {
  const allAmenitiesRustySet = new Set(Object.values(amenityCoreToRustMapping))
  const enabledAmenitiesArray: string[] = []
  amenity?.forEach((a) => {
    if (amenityCoreToRustMapping[a]) {
      enabledAmenitiesArray.push(amenityCoreToRustMapping[a])
    }
    if (allAmenitiesRustySet.has(amenityCoreToRustMapping[a])) {
      allAmenitiesRustySet.delete(amenityCoreToRustMapping[a])
    }
  })
  const disabledAmenities = Array.from(allAmenitiesRustySet).reduce((a, v) => ({ ...a, [v]: false }), {})
  const enabledAmenities = Array.from(enabledAmenitiesArray).reduce((a, v) => ({ ...a, [v]: true }), {})
  return { ...disabledAmenities, ...enabledAmenities }
}

export function transformRustyAmenityToFilters(potentialBooking: PotentialBooking): string[] {
  const amenityRustToCoreMapping = objectFlip(amenityCoreToRustMapping)
  const enabledAmenities: string[] = []
  Object.keys(potentialBooking).forEach((a) => {
    if (potentialBooking[a] && amenityRustToCoreMapping[a]) {
      enabledAmenities.push(amenityRustToCoreMapping[a])
    }
  })
  return enabledAmenities
}

export function getPDPLinkQueryParams(filters: HomeFilters, home: RecommendedHomeRow) {
  const { earliestAvailable } = home
  const _earliestAvailableDate =
    earliestAvailable && earliestAvailable.length > 0 ? new Date(earliestAvailable) : undefined

  const { moveInStartDate, moveOutStartDate, moveOutEndDate, reservationType, displayCity } = filters
  const _endDate = moveOutEndDate || moveOutStartDate
  const _startDate =
    moveInStartDate && _earliestAvailableDate && moveInStartDate < _earliestAvailableDate
      ? _earliestAvailableDate
      : moveInStartDate || _earliestAvailableDate

  const queryParamsArray: string[] = []
  if (_startDate) {
    const startDate = formatDate(_startDate, 'yyyy-MM-dd')
    queryParamsArray.push(`startDate=${startDate}`)
    if (reservationType === ReservationType.Flexible || reservationType === ReservationType.Standby) {
      queryParamsArray.push(`endDate=indefinite`)
    } else if (_endDate) {
      const endDate = formatDate(_endDate, 'yyyy-MM-dd')
      queryParamsArray.push(`endDate=${endDate}`)
    }
  }

  if (displayCity && displayCity.slug) {
    queryParamsArray.push(`searchedCity=${displayCity.slug}`)
  }

  if (reservationType === ReservationType.Standby) {
    queryParamsArray.push(`standby=true`)
  }

  const initialVal = queryParamsArray.length ? '?' : ''
  return queryParamsArray.reduce(
    (accumulator, currentValue, index) => (index > 0 ? accumulator + '&' + currentValue : accumulator + currentValue),
    initialVal,
  )
}

export function getNMTLinkQueryParams(filters: HomeFilters, home: RecommendedHomeRow) {
  const { earliestAvailable } = home
  const { moveInStartDate, moveOutEndDate, moveOutStartDate, reservationType } = filters
  const _earliestAvailableDate =
    earliestAvailable && earliestAvailable.length > 0 ? new Date(earliestAvailable) : undefined
  const _endDate = moveOutEndDate || moveOutStartDate
  const _startDate =
    moveInStartDate && _earliestAvailableDate && moveInStartDate < _earliestAvailableDate
      ? _earliestAvailableDate
      : moveInStartDate || _earliestAvailableDate
  if (!_startDate) return
  const startDate = formatDate(_startDate, 'yyyy-MM-dd')
  const endDate = _endDate ? formatDate(_endDate, 'yyyy-MM-dd') : undefined
  const isNotStandardDates = reservationType === ReservationType.Flexible || reservationType === ReservationType.Standby

  const endDateParam = isNotStandardDates || !endDate ? '' : `&endDate=${endDate}`
  return `?home=${home.id}&startDate=${startDate}${endDateParam}`
}

const convertToDate = (d: string | null): Date | undefined => {
  const date = parseDate(d)
  if (date && isValid(date)) {
    return date
  }
  return undefined
}

export function transformRustyPotentialBookingtoFilters(
  potentialBooking: PotentialBooking,
  filters: HomeFilters,
): HomeFilters {
  const {
    beginningMoveInDate,
    beginningMoveOutDate,
    endingMoveInDate,
    endingMoveOutDate,
    notes,
    minimumPrice,
    maximumPrice,
    potentialBookingSource,
    potentialBookingSourceId,
    potentialBookingStatus,
    potentialBookingStatusId,
    agent,
    agentId,
    reservationType,
    bathrooms,
    bedrooms,
    bookablePerson,
    potentialCompetitors,
  } = potentialBooking

  const status =
    potentialBookingStatus && potentialBookingStatusId
      ? {
          id: potentialBookingStatusId,
          description: potentialBookingStatus,
        }
      : undefined

  const source =
    potentialBookingSource && potentialBookingSourceId
      ? {
          id: potentialBookingSourceId,
          description: potentialBookingSource,
        }
      : undefined

  const utmSource = bookablePerson?.utmSource ? bookablePerson?.utmSource : undefined

  const bookingAgent = agent && agentId ? { id: agentId, name: agent?.name } : undefined

  const createdTime = formatDate(new Date(potentialBooking.createdAt || ''), 'MM-dd-yyyy, h:mmaaa')
  return {
    ...filters,
    displayCity: potentialBooking.displayCity,
    amenity: transformRustyAmenityToFilters(potentialBooking),
    source,
    status,
    agent: bookingAgent,
    moveInStartDate: convertToDate(beginningMoveInDate),
    moveOutStartDate: convertToDate(beginningMoveOutDate),
    moveInEndDate: convertToDate(endingMoveInDate),
    moveOutEndDate: convertToDate(endingMoveOutDate),
    notes: notes || undefined,
    minPrice: minimumPrice || undefined,
    maxPrice: maximumPrice || undefined,
    createdAt: createdTime || undefined,
    reservationType: reservationType || undefined,
    beds: bedrooms || undefined,
    baths: bathrooms || undefined,
    utmSource,
    potentialCompetitors: potentialCompetitors || undefined,
  }
}

const amenityCoreToRustMapping = {
  elevator: 'elevator',
  pets: 'petFriendly',
  parking: 'parking',
  pool: 'pool',
  air_conditioned: 'airConditioning',
  gym: 'gym',
  balcony: 'balcony',
  in_unit_laundry: 'inUnitLaundry',
}

const objectFlip = (obj: Object) => {
  return Object.entries(obj).reduce((ret, entry) => {
    const [key, value] = entry
    ret[value] = key
    return ret
  }, {})
}

export const getMutationsVariables = (filters: HomeFilters | null) => {
  const {
    displayCity,
    reservationType,
    beds,
    baths,
    source,
    minPrice,
    maxPrice,
    moveInEndDate,
    moveInStartDate,
    moveOutStartDate,
    moveOutEndDate,
    notes,
    potentialCompetitors,
  } = filters || {}

  const price =
    minPrice || maxPrice
      ? {
          minimum: minPrice,
          maximum: maxPrice,
        }
      : null

  const moveInDate =
    moveInStartDate && moveInEndDate
      ? {
          start: formatDate(moveInStartDate, 'yyyy-MM-dd'),
          end: formatDate(moveInEndDate, 'yyyy-MM-dd'),
        }
      : moveInStartDate
        ? {
            start: formatDate(moveInStartDate, 'yyyy-MM-dd'),
            end: formatDate(moveInStartDate, 'yyyy-MM-dd'),
          }
        : undefined

  const moveOutDate =
    moveOutStartDate && moveOutEndDate
      ? {
          start: formatDate(moveOutStartDate, 'yyyy-MM-dd'),
          end: formatDate(moveOutEndDate, 'yyyy-MM-dd'),
        }
      : moveOutStartDate
        ? {
            start: formatDate(moveOutStartDate, 'yyyy-MM-dd'),
            end: formatDate(moveOutStartDate, 'yyyy-MM-dd'),
          }
        : undefined

  return {
    displayCityId: displayCity?.id,
    reservationType: reservationType && reservationType.length > 0 ? reservationType : null,
    bedrooms: beds,
    potentialBookingSourceId: source?.id,
    bathrooms: baths,
    price,
    moveInDate,
    moveOutDate,
    notes,
    potentialCompetitors: potentialCompetitors && potentialCompetitors.length > 0 ? potentialCompetitors : [],
  }
}
